import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { X, Calendar, Clock, MapPin, Users, Trash2, Search, User } from "lucide-react";
import toastUtils from "../../../utils/toast";
import { Input } from "../../../shadcn/ui/input";
import { Button } from "../../../shadcn/ui/button";
import { Switch } from "../../../shadcn/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../../../shadcn/ui/popover";
import { Calendar as ShadcnCalendar } from "../../../shadcn/ui/calendar";
import { cn } from "../../../lib/utils";
import { apiV2Service } from "../../../services/apiV2Service";
import { getCurrentUserOrganizationId } from "../host-clients/utils/hostClientsUtils";
import { showApiMessage } from "../../../utils/messageHandler";
import { convertToUTC } from "../../../utils/timezoneUtils";
import availabilityService from "../../../services/availabilityService";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import * as localService from "../../../services/localService";
import HUpgradeButton from "../../../hcomponents/HUpgradeButton";
import logger from "../../../utils/logger";


const ModifyBookingModal = ({
  isOpen,
  onClose,
  booking,
  selectedWorkspace,
  workspaces = [],
  onUpdateBooking,
  availableTimeSlots = [],
  existingBookings = [],
  timeFormat = "12h",
  canBookForCustomer = false,
}) => {
  const isCheckedIn = booking?.status === "CHECKED_IN";
  const [formData, setFormData] = useState({
    workspaceId: "",
    description: "",
    startTime: null,
    durationMinutes: null,
    emails: [],
  });
  const [emailInput, setEmailInput] = useState("");
  const [attendeeCache, setAttendeeCache] = useState({});
  const [originalDurationMinutes, setOriginalDurationMinutes] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchMode, setSearchMode] = useState("staff");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [hasInitialized, setHasInitialized] = useState(false);

  // Availability state
  const [availabilityData, setAvailabilityData] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const availabilityCache = useRef({});

  // Date selection state
  const [selectedDate, setSelectedDate] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Derived state - current workspace being edited
  const currentWorkspace = workspaces.find(w => w.id.toString() === formData.workspaceId) || selectedWorkspace;

  // Total bookable instances for multi-instance spaces
  const totalInstances = useMemo(() => {
    if (!currentWorkspace) return 1;
    return (
      currentWorkspace.totalInstances || currentWorkspace.numberOfInstances || currentWorkspace.instances?.length || 1
    );
  }, [currentWorkspace]);

  // Existing bookings filtered to current workspace only
  const filteredExistingBookings = useMemo(() => {
    if (!currentWorkspace?.id || !existingBookings?.length) return [];
    const wsId = currentWorkspace.id.toString();
    return existingBookings.filter(b => b.spaceId?.toString() === wsId || b.space?.id?.toString() === wsId);
  }, [currentWorkspace?.id, existingBookings]);

  // Get bookings overlapping a specific date
  const getBookingsForDate = useCallback(
    date => {
      if (!filteredExistingBookings.length) return [];
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      return filteredExistingBookings.filter(b => {
        const bStart = new Date(b.startTime);
        const bEnd = new Date(b.endTime);
        return bStart < dayEnd && bEnd >= dayStart;
      });
    },
    [filteredExistingBookings]
  );

  // Calendar day coloring: fully booked dates (all instances busy that day)
  const bookedDatesSet = useMemo(() => {
    const set = new Set();
    if (!filteredExistingBookings.length || totalInstances <= 0) return set;
    const busyCountByDate = {};
    filteredExistingBookings.forEach(b => {
      const bStart = new Date(b.startTime);
      const bEnd = new Date(b.endTime);
      const current = new Date(bStart);
      current.setHours(0, 0, 0, 0);
      const endDate = new Date(bEnd);
      endDate.setHours(0, 0, 0, 0);
      while (current <= endDate) {
        const dStr = current.toDateString();
        if (!busyCountByDate[dStr]) busyCountByDate[dStr] = new Set();
        busyCountByDate[dStr].add(b.spaceInstanceId || `b-${b.id}`);
        current.setDate(current.getDate() + 1);
      }
    });
    Object.entries(busyCountByDate).forEach(([dStr, busySet]) => {
      if (busySet.size >= totalInstances) set.add(dStr);
    });
    return set;
  }, [filteredExistingBookings, totalInstances]);

  // Calendar day coloring: dates with at least one available time slot
  const availableDatesSet = useMemo(() => {
    const set = new Set();
    if (!currentWorkspace?.availableSlots?.length) return set;

    const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 90; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayName = dayNames[date.getDay()];
      const daySlots = currentWorkspace.availableSlots.filter(s => s.day === dayName);
      if (!daySlots.length) continue;

      let hasAvailableSlot = false;
      for (const slot of daySlots) {
        const [startH, startM] = (slot.startTime || "00:00").split(":").map(Number);
        const [endH, endM] = (slot.endTime || "23:59").split(":").map(Number);
        const startMin = startH * 60 + startM;
        const endMin = endH * 60 + endM;

        for (let m = startMin; m < endMin; m += 30) {
          const slotStart = new Date(date);
          slotStart.setHours(Math.floor(m / 60), m % 60, 0, 0);
          const slotEnd = new Date(slotStart);
          slotEnd.setMinutes(slotEnd.getMinutes() + (originalDurationMinutes || 60));

          if (slotEnd.getHours() * 60 + slotEnd.getMinutes() > endMin) continue;

          const busyIds = new Set();
          filteredExistingBookings.forEach(b => {
            const bStart = new Date(b.startTime).getTime();
            const bEnd = new Date(b.endTime).getTime();
            if (slotStart.getTime() < bEnd && slotEnd.getTime() > bStart) {
              busyIds.add(b.spaceInstanceId || `b-${b.id}`);
            }
          });

          if (busyIds.size < totalInstances) {
            hasAvailableSlot = true;
            break;
          }
        }
        if (hasAvailableSlot) break;
      }
      if (hasAvailableSlot) set.add(date.toDateString());
    }
    return set;
  }, [currentWorkspace, filteredExistingBookings, totalInstances, originalDurationMinutes]);

  const calendarModifiersStyles = useMemo(
    () => ({
      booked: { backgroundColor: "#fee2e2", color: "#dc2626", fontWeight: "bold" },
      partial: { backgroundColor: "#ffedd5", color: "#9a3412", fontWeight: "bold" },
      available: { backgroundColor: "#dcfce7", color: "#166534", fontWeight: "bold" },
    }),
    []
  );

  const calendarModifiers = useMemo(
    () => ({
      booked: date => bookedDatesSet.has(date.toDateString()),
      partial: date => {
        if (bookedDatesSet.has(date.toDateString())) return false;
        const bookings = getBookingsForDate(date);
        return bookings.length > 0;
      },
      available: date => {
        if (bookedDatesSet.has(date.toDateString())) return false;
        const bookings = getBookingsForDate(date);
        return availableDatesSet.has(date.toDateString()) && bookings.length === 0;
      },
    }),
    [bookedDatesSet, availableDatesSet, getBookingsForDate]
  );

  // Fetch all availability for the space when modal opens or workspace changes
  useEffect(() => {
    if (isOpen && currentWorkspace?.id) {
      fetchAllAvailability();
    }
  }, [isOpen, currentWorkspace?.id]);

  const fetchAllAvailability = async () => {
    if (!currentWorkspace?.id) return;

    const cacheKey = `all-${currentWorkspace.id}`;

    if (availabilityCache.current[cacheKey]) {
      const cached = availabilityCache.current[cacheKey];
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) {
        setAvailabilityData(cached.data);
        return;
      }
    }

    setLoadingAvailability(true);
    try {
      const data = await availabilityService.getAvailability(currentWorkspace.id);
      availabilityCache.current[cacheKey] = { data, timestamp: Date.now() };
      setAvailabilityData(data);
    } catch (error) {
      logger.error("Failed to fetch availability:", error);
      setAvailabilityData({ bookedSlots: [], instanceIds: [] });
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Build set of fully-booked dates from availability API data
  const fullyBookedDatesFromApi = useMemo(() => {
    const set = new Set();
    if (!availabilityData?.bookedSlots?.length || !availabilityData?.totalInstances) return set;
    const busyCountByDate = {};
    availabilityData.bookedSlots.forEach(slot => {
      const start = new Date(slot.startTime);
      const end = new Date(slot.endTime);
      const current = new Date(start);
      current.setHours(0, 0, 0, 0);
      const endDate = new Date(end);
      endDate.setHours(0, 0, 0, 0);
      while (current <= endDate) {
        const dStr = current.toDateString();
        if (!busyCountByDate[dStr]) busyCountByDate[dStr] = new Set();
        busyCountByDate[dStr].add(slot.spaceInstanceId || `slot-${slot.startTime}-${slot.spaceInstanceId}`);
        current.setDate(current.getDate() + 1);
      }
    });
    Object.entries(busyCountByDate).forEach(([dStr, busySet]) => {
      if (busySet.size >= availabilityData.totalInstances) set.add(dStr);
    });
    return set;
  }, [availabilityData]);

  // Helper function to format time based on timeFormat prop
  const formatTime = useCallback(
    date => {
      // Validate date parameter
      if (!date) {
        logger.error("formatTime called with null/undefined date");
        return "00:00";
      }

      if (!(date instanceof Date)) {
        logger.error("formatTime called with non-Date object:", typeof date, date);
        return "00:00";
      }

      if (isNaN(date.getTime())) {
        logger.error("formatTime called with invalid Date object:", date);
        return "00:00";
      }

      try {
        if (timeFormat === "24h") {
          return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
        } else {
          return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      } catch (error) {
        logger.error("Error formatting time:", error, "Date:", date);
        return "00:00";
      }
    },
    [timeFormat]
  );

  // Generate available time slots for the selected date
  const generatedTimeSlots = useMemo(() => {
    if (!selectedDate || !currentWorkspace) return [];

    const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    const dayName = dayNames[selectedDate.getDay()];

    // Get available hours for this day from workspace config
    const dayAvailability = currentWorkspace.availableSlots?.find(s => s.day === dayName) || null;

    if (!dayAvailability) return [];

    const startTotalMin = (() => {
      const [h, m] = (dayAvailability.startTime || "00:00").split(":").map(Number);
      return h * 60 + m;
    })();

    const endTotalMin = (() => {
      const [h, m] = (dayAvailability.endTime || "23:59").split(":").map(Number);
      return h * 60 + m;
    })();

    const now = new Date();
    const todayStr = now.toDateString();
    const selectedDayStr = selectedDate.toDateString();

    const slots = [];
    for (let m = startTotalMin; m < endTotalMin; m += 30) {
      const hour = Math.floor(m / 60);
      const minute = m % 60;
      const slotDate = new Date(selectedDate);
      slotDate.setHours(hour, minute, 0, 0);

      // Skip past slots for today
      if (selectedDayStr === todayStr && slotDate <= now) continue;

      // Calculate end time = start + original duration
      const endSlotDate = new Date(slotDate);
      endSlotDate.setMinutes(endSlotDate.getMinutes() + (originalDurationMinutes || 60));

      // Check if the full duration fits within available hours
      const durationEndTotalMin = endSlotDate.getHours() * 60 + endSlotDate.getMinutes();
      if (durationEndTotalMin > endTotalMin) continue;

      // Check how many instances are busy at this time (multi-instance support)
      const currentBookingId = booking?.id;
      const busyInstanceIds = new Set();
      (availabilityData?.bookedSlots || []).forEach(slot => {
        if (slot.id === currentBookingId) return;
        const bookingStart = new Date(slot.startTime).getTime();
        const bookingEnd = new Date(slot.endTime).getTime();
        const bufferMs = (currentWorkspace.bufferTimeInMinutes || 0) * 60 * 1000;
        if (slotDate.getTime() < bookingEnd + bufferMs && endSlotDate.getTime() > bookingStart) {
          busyInstanceIds.add(slot.spaceInstanceId || `booking-${slot.id}`);
        }
      });

      const busyCount = busyInstanceIds.size;
      const isFull = busyCount >= totalInstances;

      const timeStr = formatTime(slotDate);
      slots.push({
        time: timeStr,
        dateTime: slotDate,
        isAvailable: !isFull,
        busyCount,
        isFull,
        endTime: endSlotDate,
      });
    }

    return slots;
  }, [selectedDate, currentWorkspace, availabilityData, originalDurationMinutes, booking, formatTime]);

  // Calculate duration from booking start and end times
  const calculateBookingDuration = booking => {
    if (!booking || !booking.startTime || !booking.endTime) return null;

    const startTime = new Date(booking.startTime);
    const endTime = new Date(booking.endTime);

    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return null;

    // Calculate duration in minutes with better precision handling
    const durationMs = endTime.getTime() - startTime.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));

    // Ensure minimum duration of 1 minute
    return Math.max(1, durationMinutes);
  };

  // Initialize form data when modal opens — fetch full booking details first
  useEffect(() => {
    if (isOpen && booking && !hasInitialized) {
      const init = async () => {
        let bookingData = booking;

        try {
          const res = await spaceManagementService.getBookingById(booking.id);
          if (res?.status === 200 && res?.data) {
            bookingData = res.data;
          }
        } catch (e) {
          // Fall back to prop data if fetch fails
        }

        const initialDuration = calculateBookingDuration(bookingData);
        const minDurationMinutes = currentWorkspace?.minimumDuration ? currentWorkspace.minimumDuration * 60 : 60;
        const safeDuration = Math.max(initialDuration || 0, minDurationMinutes);

        const startTimeFormatted = bookingData.startTime ? formatTime(new Date(bookingData.startTime)) : null;
        const bookingDate = new Date(bookingData.startTime);

        setSelectedDate(bookingDate);

        setFormData({
          workspaceId: bookingData.spaceId?.toString() || selectedWorkspace?.id || "",
          description: bookingData.notes || "",
          startTime: startTimeFormatted,
          durationMinutes: safeDuration,
          emails: bookingData.emails || bookingData.apiBooking?.emails || [],
        });

        const apiUsers = bookingData.apiBooking?.users || bookingData.users;
        if (apiUsers && Array.isArray(apiUsers)) {
          const initialEmails = [...(bookingData.emails || bookingData.apiBooking?.emails || [])];
          const cache = {};

          apiUsers.forEach(user => {
            const email = user.email || user.emailAddress || user.username;
            if (email && !initialEmails.includes(email)) {
              initialEmails.push(email);
            }
            if (email) {
              cache[email] = user;
            }
          });

          setFormData(prev => ({
            ...prev,
            emails: initialEmails,
          }));
          setAttendeeCache(cache);
        } else {
          setAttendeeCache({});
        }

        setOriginalDurationMinutes(safeDuration);
        setEmailInput("");
        setSearchTerm("");
        setSearchResults([]);
        setHasInitialized(true);
      };

      init();
    } else if (!isOpen) {
      setHasInitialized(false);
      setSelectedDate(null);
      setSearchTerm("");
      setSearchResults([]);
      setAttendeeCache({});
      setSelectedCustomer(null);
      setSearchMode("staff");
      setEmailInput("");
      setAvailabilityData(null);
    }
  }, [isOpen, booking, selectedWorkspace, hasInitialized, formatTime]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Unified search effect — searches customers or staff depending on searchMode toggle
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.trim().length < 1) {
      setSearchResults([]);
      setSearchError("");
      return;
    }

    const isClientsMode = canBookForCustomer && searchMode === "clients" && !selectedCustomer;
    const isStaffMode = !isClientsMode && FeatureAccess.hasAttendeeAccess(localService.getUser());

    if (!isClientsMode && !isStaffMode) {
      setSearchResults([]);
      setSearchError("");
      return;
    }

    setSearchLoading(true);
    setSearchError("");

    const organizationId = getCurrentUserOrganizationId();
    if (!organizationId) {
      setSearchError("Organization ID not found. Please refresh and try again.");
      setSearchLoading(false);
      return;
    }

    apiV2Service
      .searchUsers({
        q: searchTerm.trim(),
        limit: 10,
        status: "ACTIVE",
        ...(isClientsMode ? { mode: "CLIENTS" } : { organizationId }),
      })
      .then(res => {
        let users = [];
        if (Array.isArray(res?.data?.data)) {
          users = res.data.data;
        } else if (Array.isArray(res?.data)) {
          users = res.data;
        }
        const currentUser = localService.getUser();
        const currentUserEmail = currentUser?.email || currentUser?.emailAddress || currentUser?.username;
        if (currentUserEmail) {
          users = users.filter(u => (u.email || u.emailAddress || u.username) !== currentUserEmail);
        }
        setSearchResults(users);
      })
      .catch(err => {
        logger.error("Search error:", err);
        setSearchError("Failed to search");
        setSearchResults([]);
      })
      .finally(() => setSearchLoading(false));
  }, [searchTerm, canBookForCustomer, searchMode, selectedCustomer]);

  const handleSelectSearchResult = user => {
    const email = user.email || user.emailAddress || user.username;
    if (!email) {
      toastUtils.error("User does not have an email address");
      return;
    }

    if (formData.emails.includes(email)) {
      toastUtils.warning("User already added");
      return;
    }

    setAttendeeCache(prev => ({
      ...prev,
      [email]: user,
    }));

    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, email],
    }));

    if (searchMode === "clients" && !selectedCustomer) {
      setSelectedCustomer(user);
    }

    setSearchTerm("");
    setSearchResults([]);
  };

  const handleRemoveAttendee = email => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.filter(e => e !== email),
    }));
    // We can keep it in cache, it doesn't hurt
  };

  const isFormValid = () => {
    const hasWorkspace = !!formData.workspaceId;
    const hasDate = !!selectedDate;
    const hasStartTime = !!formData.startTime;
    const minDurationMinutes = currentWorkspace?.minimumDuration ? currentWorkspace.minimumDuration * 60 : 60;
    const hasDuration = formData.durationMinutes && formData.durationMinutes >= minDurationMinutes;

    return hasWorkspace && hasDate && hasStartTime && hasDuration;
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Invalid email format" },
          },
        },
        "validate email"
      );
      return;
    }

    if (formData.emails.includes(email)) {
      showApiMessage(
        {
          response: { status: 400, data: { message: "Email already added" } },
        },
        "validate email"
      );
      return;
    }

    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, email],
    }));
    setEmailInput("");
  };

  const handleEmailKeyPress = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleStartTimeSelect = startTime => {
    setFormData(prev => ({
      ...prev,
      startTime: startTime,
    }));
  };

  const handleSave = () => {
    if (!booking || !booking.id) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Booking information is missing" },
          },
        },
        "validate booking"
      );
      return;
    }

    if (!selectedDate) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Please select a date" },
          },
        },
        "validate date"
      );
      return;
    }

    if (!isFormValid()) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Please complete all required fields" },
          },
        },
        "validate form"
      );
      return;
    }

    // Parse the start time
    const parseTimeForSave = timeStr => {
      try {
        if (timeStr.includes("AM") || timeStr.includes("PM")) {
          const [time, period] = timeStr.split(" ");
          let [hours, minutes] = time.split(":").map(Number);

          if (isNaN(hours) || isNaN(minutes)) {
            throw new Error(`Invalid time format: ${timeStr}`);
          }

          if (period === "PM" && hours !== 12) {
            hours += 12;
          } else if (period === "AM" && hours === 12) {
            hours = 0;
          }

          return { hours, minutes };
        } else {
          const [hours, minutes] = timeStr.split(":").map(Number);

          if (isNaN(hours) || isNaN(minutes)) {
            throw new Error(`Invalid time format: ${timeStr}`);
          }

          return { hours, minutes };
        }
      } catch (error) {
        logger.error("Error parsing time in handleSave:", error.message);
        return null;
      }
    };

    const parsedTime = parseTimeForSave(formData.startTime);
    if (!parsedTime) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Invalid start time format" },
          },
        },
        "validate time"
      );
      return;
    }

    const { hours: startHour, minutes: startMinute } = parsedTime;

    // Create new start and end times using selectedDate
    const newStartDateTime = new Date(selectedDate);
    newStartDateTime.setHours(startHour, startMinute, 0, 0);

    const newEndDateTime = new Date(newStartDateTime);
    newEndDateTime.setMinutes(newEndDateTime.getMinutes() + (originalDurationMinutes || formData.durationMinutes));

    // VALIDATION: Do not allow extending beyond original duration
    if (originalDurationMinutes != null) {
      const newDuration = formData.durationMinutes;
      if (newDuration > originalDurationMinutes) {
        showApiMessage(
          {
            response: {
              status: 400,
              data: {
                message: "New duration cannot exceed the original booking duration.",
              },
            },
          },
          "update booking"
        );
        return;
      }
    }

    // VALIDATION: Check if the new booking fits within available slots
    if (currentWorkspace?.availableSlots && currentWorkspace.availableSlots.length > 0) {
      const bookingDate = selectedDate;
      // Use uppercase day name to match the format in availableSlots (e.g., "MONDAY" instead of "Monday")
      const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
      const dayName = dayNames[bookingDate.getDay()];

      // Find availability for the booking's day
      const availabilityForDay = currentWorkspace.availableSlots.find(slot => slot.day === dayName);

      if (availabilityForDay) {
        // Parse available slot times
        const parseTimeToHours = timeStr => {
          const [hours, minutes] = timeStr.split(":").map(Number);
          return hours + minutes / 60;
        };

        const availableStartHour = parseTimeToHours(availabilityForDay.startTime);
        const availableEndHour = parseTimeToHours(availabilityForDay.endTime);

        // Convert new booking times to hours for comparison
        const newStartHour = newStartDateTime.getHours() + newStartDateTime.getMinutes() / 60;
        const newEndHour = newEndDateTime.getHours() + newEndDateTime.getMinutes() / 60;

        // Check if booking falls within available hours
        if (newStartHour < availableStartHour || newEndHour > availableEndHour) {
          showApiMessage(
            {
              response: {
                status: 400,
                data: {
                  message: `Booking time is outside available hours. ${currentWorkspace.name} is only available from ${availabilityForDay.startTime} to ${availabilityForDay.endTime} on ${dayName}s.`,
                },
              },
            },
            "validate booking"
          );
          return;
        }
      } else {
        showApiMessage(
          {
            response: {
              status: 400,
              data: {
                message: `No availability found for this day. ${currentWorkspace.name} is not available on ${dayName}s.`,
              },
            },
          },
          "validate booking"
        );
        return;
      }
    }

    // VALIDATION: Check for conflicts with other existing bookings
    const bookingsToCheck = availabilityData?.bookedSlots || existingBookings;
    if (bookingsToCheck && bookingsToCheck.length > 0) {
      // Filter out the current booking being modified
      const otherBookings = bookingsToCheck.filter(
        existingBooking =>
          existingBooking.id !== booking.id &&
          (existingBooking.spaceId === booking.spaceId || existingBooking.spaceInstanceId)
      );

      // Get buffer time from workspace
      const bufferTimeInMinutes = currentWorkspace?.bufferTimeInMinutes || 0;
      const bufferMs = bufferTimeInMinutes * 60 * 1000;

      // Check for time conflicts (including buffer time)
      const hasConflict = otherBookings.some(existingBooking => {
        const existingStart = new Date(existingBooking.startTime).getTime();
        const existingEnd = new Date(existingBooking.endTime).getTime();
        const newStartTime = newStartDateTime.getTime();
        const newEndTime = newEndDateTime.getTime();

        // Apply buffer time to existing booking
        const existingStartWithBuffer = existingStart - bufferMs;
        const existingEndWithBuffer = existingEnd + bufferMs;

        // Check if the new booking overlaps with any existing booking (including buffer)
        // Overlap occurs if: new range starts before existing ends (with buffer) AND new range ends after existing starts (with buffer)
        return newStartTime < existingEndWithBuffer && newEndTime > existingStartWithBuffer;
      });

      if (hasConflict) {
        showApiMessage(
          {
            response: {
              status: 400,
              data: {
                message:
                  "Booking time conflicts with existing booking. The selected time overlaps with another booking. Please choose a different time.",
              },
            },
          },
          "validate booking"
        );
        return;
      }
    }

    const updatePayload = {
      emails: formData.emails,
      newStartTime: convertToUTC(newStartDateTime),
      newEndTime: convertToUTC(newEndDateTime),
      notes: formData.description || undefined,
    };

    logger.log("Updating booking with payload:", updatePayload);
    onUpdateBooking(booking.id, updatePayload);
  };

  const formatDate = date => {
    const validDate = date instanceof Date ? date : new Date(date);

    if (isNaN(validDate.getTime())) {
      return "Invalid Date";
    }

    return validDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  const formatTimeDisplay = () => {
    if (!formData.startTime || !formData.durationMinutes) return "No time selected";

    const displayDate = selectedDate || (booking ? new Date(booking.startTime) : new Date());
    if (isNaN(displayDate.getTime())) {
      return "Invalid date";
    }

    const parseTimeString = timeStr => {
      try {
        if (timeStr.includes("AM") || timeStr.includes("PM")) {
          const [time, period] = timeStr.split(" ");
          let [hours, minutes] = time.split(":").map(Number);

          if (isNaN(hours) || isNaN(minutes)) {
            throw new Error(`Invalid time format: ${timeStr}`);
          }

          if (period === "PM" && hours !== 12) {
            hours += 12;
          } else if (period === "AM" && hours === 12) {
            hours = 0;
          }

          return { hours, minutes };
        } else {
          const [hours, minutes] = timeStr.split(":").map(Number);

          if (isNaN(hours) || isNaN(minutes)) {
            throw new Error(`Invalid time format: ${timeStr}`);
          }

          return { hours, minutes };
        }
      } catch (error) {
        logger.error("Error parsing time in formatTimeDisplay:", error.message);
        return { hours: 0, minutes: 0 };
      }
    };

    const { hours: startHour, minutes: startMinute } = parseTimeString(formData.startTime);
    const startDateTime = new Date(displayDate);
    startDateTime.setHours(startHour, startMinute, 0, 0);

    if (isNaN(startDateTime.getTime())) {
      return "Invalid start time";
    }

    const endDateTime = new Date(startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + formData.durationMinutes);

    if (isNaN(endDateTime.getTime())) {
      return "Invalid end time";
    }

    const startTimeStr = formatTime(startDateTime);
    const endTimeStr = formatTime(endDateTime);

    return `${startTimeStr} - ${endTimeStr}`;
  };

  if (!isOpen || !booking) return null;

  return (
    <div
      className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100">
        <div className="relative">
          <div className="flex justify-between items-center p-6 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Modify Booking</h2>
              <p className="text-xs text-gray-500 mt-1">Update reservation details for this space</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {isCheckedIn && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                <div className="text-blue-500 mt-0.5">ℹ️</div>
                <div className="text-sm text-blue-800">Checked-in booking: Only end time can be modified</div>
              </div>
            )}

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Workspace</label>
              <select
                value={formData.workspaceId}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    workspaceId: e.target.value,
                  }))
                }
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled // Workspace cannot be changed for existing bookings
              >
                <option value="">Select a workspace</option>
                {workspaces.map(workspace => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Workspace cannot be changed for existing bookings</p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Booking Notes <span className="text-xs font-normal text-gray-500">(Optional)</span>
              </label>
              <textarea
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add any special requirements or notes for this booking..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all resize-none text-sm"
              />
            </div>

            {/* Attendees */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Attendees <span className="text-xs font-normal text-gray-500">(Optional)</span>
              </label>

              <div className="space-y-3">
                {/* Direct email input - always visible */}
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter email address and press Enter..."
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyPress={handleEmailKeyPress}
                    className="flex-1"
                  />
                </div>

                {/* Search toggle and input */}
                <div className="space-y-2">
                  {canBookForCustomer ? (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {searchMode === "staff" ? "Search staff" : "Search clients"}
                      </span>
                      <Switch
                        checked={searchMode === "clients"}
                        onCheckedChange={checked => setSearchMode(checked ? "clients" : "staff")}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Book for customers</span>
                      <HUpgradeButton size="sm" />
                    </div>
                  )}
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder={
                        searchMode === "clients" && !selectedCustomer
                          ? "Search customer by name or email..."
                          : "Search staff by name or email..."
                      }
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="w-full pr-8 text-sm"
                    />
                    <Search className="absolute right-2 top-2.5 h-3 w-3 text-gray-400 pointer-events-none" />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchTerm("");
                          setSearchResults([]);
                        }}
                        className="absolute right-7 top-2.5 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Clear search"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {searchLoading && <div className="text-xs text-gray-500">Searching...</div>}
                  {searchError && <div className="text-xs text-red-500">{searchError}</div>}
                  {searchResults.length > 0 && (
                    <div className="overflow-y-auto absolute z-50 w-full max-h-48 bg-white rounded-md border border-gray-200 shadow-lg">
                      {searchResults.map((user, index) => {
                        const displayName =
                          user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.name || user.username || "Unknown User";
                        const email = user.email || user.emailAddress || user.username;
                        const isSelected = formData.emails.includes(email);

                        return (
                          <div
                            key={index}
                            className={`flex items-center px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                              isSelected ? "bg-gray-50 opacity-60" : "hover:bg-blue-50"
                            }`}
                            onClick={() => !isSelected && handleSelectSearchResult(user)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">{displayName}</div>
                              <div className="text-xs text-gray-500 truncate">{email}</div>
                            </div>
                            {isSelected && <div className="ml-2 text-xs text-blue-600 font-medium">Added</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {searchTerm.trim() && !searchLoading && searchResults.length === 0 && !searchError && (
                    <p className="text-xs text-gray-500">No results found.</p>
                  )}
                </div>

                {/* Selected customer chip */}
                {selectedCustomer && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-blue-900">
                          {selectedCustomer.firstName} {selectedCustomer.lastName}
                        </div>
                        <div className="text-xs text-blue-600">{selectedCustomer.emailAddress}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setSearchTerm("");
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Change
                    </button>
                  </div>
                )}
                {selectedCustomer && (
                  <p className="text-xs text-gray-500">
                    Booking will be created for {selectedCustomer.firstName} {selectedCustomer.lastName}
                  </p>
                )}

                {/* Selected Attendees List */}
                {formData.emails.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Current Attendees</div>

                    {formData.emails.map(email => {
                      const user = attendeeCache[email];
                      const displayName = user
                        ? user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.username || user.name || email
                        : email;

                      return (
                        <div
                          key={email}
                          className={`flex items-center justify-between p-2 rounded-xl border ${user ? "bg-blue-50/50 border-blue-100" : "bg-gray-50 border-gray-200"}`}
                        >
                          <div className="flex items-center">
                            {user ? (
                              <img
                                src={
                                  user.imageUrl ||
                                  user.avatar ||
                                  `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random&color=fff`
                                }
                                alt={displayName}
                                className="w-7 h-7 rounded-full mr-2 border border-blue-200"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
                                <Users className="w-3.5 h-3.5 text-gray-500" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className={`text-xs font-bold truncate ${user ? "text-blue-900" : "text-gray-700"}`}>
                                {displayName}
                              </div>
                              {user && <div className="text-[10px] text-blue-600 truncate">{email}</div>}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveAttendee(email)}
                            className={`p-1.5 rounded-lg transition-all ${user ? "text-blue-400 hover:text-red-500 hover:bg-red-50" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Date & Time Selection */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-8 h-8 bg-indigo-600 rounded-full">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !selectedDate && "text-muted-foreground"
                        )}
                        disabled={isCheckedIn}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {selectedDate ? formatDate(selectedDate) : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <ShadcnCalendar
                        mode="single"
                        selected={selectedDate || undefined}
                        onSelect={date => {
                          if (date) {
                            setSelectedDate(date);
                            setIsCalendarOpen(false);
                            setFormData(prev => ({ ...prev, startTime: null }));
                          }
                        }}
                        disabled={date => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          if (date < today) return true;
                          const dayNames = [
                            "SUNDAY",
                            "MONDAY",
                            "TUESDAY",
                            "WEDNESDAY",
                            "THURSDAY",
                            "FRIDAY",
                            "SATURDAY",
                          ];
                          const dayName = dayNames[date.getDay()];
                          const hasAvailability = currentWorkspace?.availableSlots?.some(s => s.day === dayName);
                          if (!hasAvailability) return true;
                          if (bookedDatesSet.has(date.toDateString())) return true;
                          if (fullyBookedDatesFromApi.has(date.toDateString())) return true;
                          return false;
                        }}
                        modifiers={calendarModifiers}
                        modifiersStyles={calendarModifiersStyles}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center text-sm text-indigo-600 font-medium whitespace-nowrap">
                  <Clock className="mr-1 w-3 h-3" />
                  {formatTimeDisplay()}
                </div>
              </div>

              {/* Available Start Times */}
              {!isCheckedIn && (
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-900">Choose a new start time</label>
                  {loadingAvailability ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading availability...</span>
                    </div>
                  ) : generatedTimeSlots.length > 0 ? (
                    <div className="overflow-x-auto pb-2 -mx-1 px-1">
                      <div className="flex gap-2 min-w-max">
                        {generatedTimeSlots.map((slot, index) => {
                          const isSelected = formData.startTime === slot.time;
                          const isPartial = slot.isAvailable && slot.busyCount > 0;
                          return (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                if (slot.isAvailable) {
                                  handleStartTimeSelect(slot.time);
                                }
                              }}
                              disabled={!slot.isAvailable}
                              className={cn(
                                "px-4 py-2.5 text-sm font-medium rounded-lg border-2 transition-all whitespace-nowrap flex-shrink-0",
                                isSelected
                                  ? "bg-blue-600 border-blue-600 text-white shadow-lg"
                                  : slot.isFull
                                    ? "bg-red-100 border-red-300 text-red-500 cursor-not-allowed opacity-60"
                                    : isPartial
                                      ? "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 hover:border-orange-300"
                                      : "bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300"
                              )}
                            >
                              <div className="font-semibold">{slot.time}</div>
                              {isPartial && (
                                <div className="text-[10px] opacity-75">
                                  {totalInstances - slot.busyCount}/{totalInstances} left
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No available time slots for this date.</p>
                  )}
                </div>
              )}
              {isCheckedIn && <p className="text-xs text-blue-600">🔒 Start time is locked for checked-in bookings</p>}
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden group">
              <div className="flex justify-between items-center relative z-10">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                    Booking Duration
                  </label>
                  <div className="text-lg font-bold text-slate-900">
                    {(() => {
                      const h = Math.floor(originalDurationMinutes / 60);
                      const m = originalDurationMinutes % 60;
                      let label = "";
                      if (h > 0) label += `${h}h `;
                      if (m > 0 || h === 0) label += `${m}m`;
                      return label.trim();
                    })()}
                  </div>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500 shadow-sm border border-blue-100">
                  <Clock className="w-5 h-5" />
                </div>
              </div>
              <p className="mt-2 text-[10px] text-slate-400 font-medium relative z-10">
                🔒 Duration is fixed for existing bookings
              </p>
              {/* Subtle background decoration */}
              <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-blue-500/5 rounded-full blur-2xl" />
            </div>

            {currentWorkspace && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex justify-center items-center w-8 h-8 bg-indigo-600 rounded-full">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{currentWorkspace.name}</div>
                    <div className="text-sm text-gray-600">{currentWorkspace.location}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!isFormValid() && (
            <div className="p-3 mt-6 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="text-sm text-yellow-800">
                <strong>Complete the form to update booking:</strong>
                <ul className="mt-1 ml-4 list-disc">
                  {!formData.workspaceId && <li>Select a workspace</li>}
                  {!selectedDate && <li>Select a date</li>}
                  {selectedDate && !formData.startTime && <li>Choose a start time</li>}
                  {(!formData.durationMinutes ||
                    (currentWorkspace && formData.durationMinutes < (currentWorkspace.minimumDuration || 1) * 60)) && (
                    <li>
                      Set duration (minimum {currentWorkspace?.minimumDuration || 1} hour
                      {currentWorkspace?.minimumDuration !== 1 ? "s" : ""})
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

          <div className="flex justify-end p-6 border-t border-gray-50 gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid()}
              className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all ${
                isFormValid()
                  ? "text-white bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-100"
                  : "text-gray-400 bg-gray-100 cursor-not-allowed"
              }`}
            >
              Update Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModifyBookingModal;
